import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Plus, Upload, Trash2, RefreshCw, UserCheck, UserX, PauseCircle, Link as LinkIcon, Copy, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800",
  inactive: "bg-slate-200 text-slate-600",
  suspended: "bg-rose-100 text-rose-800",
};
const STATUS_LABELS = { active: "Actif", inactive: "Inactif", suspended: "Suspendu" };

export default function OrgMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [addForm, setAddForm] = useState({ first_name: "", last_name: "", email: "", phone: "", internal_ref: "", status: "active" });
  const [addOpen, setAddOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ms, il] = await Promise.all([
        api.get(`/org/members${tab !== "all" ? `?status=${tab}` : ""}`),
        api.get("/org/invite-link"),
      ]);
      setMembers(ms.data.items || []);
      setInviteLink(il.data.link || "");
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = q ? members.filter((m) => {
    const qq = q.toLowerCase();
    return `${m.first_name} ${m.last_name}`.toLowerCase().includes(qq)
        || (m.email || "").toLowerCase().includes(qq)
        || (m.internal_ref || "").toLowerCase().includes(qq);
  }) : members;

  const addMember = async (e) => {
    e.preventDefault();
    setBusyId("__new__");
    try {
      await api.post("/org/members", addForm);
      toast.success("Membre ajouté.");
      setAddForm({ first_name: "", last_name: "", email: "", phone: "", internal_ref: "", status: "active" });
      setAddOpen(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur."); }
    finally { setBusyId(""); }
  };

  const changeStatus = async (m, status) => {
    setBusyId(m.id);
    try { await api.patch(`/org/members/${m.id}`, { status }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusyId(""); }
  };

  const softDelete = async (m) => {
    if (!confirm(`Désactiver ${m.first_name} ${m.last_name} ? (son historique sera conservé)`)) return;
    setBusyId(m.id);
    try { await api.delete(`/org/members/${m.id}`); toast.success("Membre désactivé."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusyId(""); }
  };

  const uploadCSV = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/org/members/import-csv", fd);
      setImportResult(data);
      toast.success(`${data.imported} membre(s) importé(s).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'import."); }
    finally { setImporting(false); }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(inviteLink);
    toast.success("Lien copié dans le presse-papiers.");
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    try {
      await api.post("/org/invitations", { email: inviteEmail });
      toast.success("Invitation envoyée !");
      setInviteEmail("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur."); }
  };

  const counts = members.reduce((acc, m) => { acc[m.status] = (acc[m.status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="org-members-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="org-members-title">
            <Users className="text-emerald-700" size={26}/> Mes membres
          </h1>
          <p className="text-slate-500 text-xs mt-1">Ajoutez et gérez les personnes qui bénéficient des avantages de votre organisme.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex">
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e)=>uploadCSV(e.target.files?.[0])} disabled={importing} data-testid="org-members-csv-input"/>
            <Button asChild variant="outline" disabled={importing} data-testid="org-members-csv-btn"><span><Upload size={14} className="mr-1"/> {importing ? "Import..." : "Import CSV"}</span></Button>
          </label>
          <Button onClick={()=>setAddOpen((v)=>!v)} className="btn-shine text-white" data-testid="org-members-add-btn"><Plus size={14} className="mr-1"/> Ajouter un membre</Button>
        </div>
      </div>

      {/* Import result */}
      {importResult && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs" data-testid="org-members-import-result">
          <div><b>{importResult.imported}</b> importés · <b>{importResult.duplicates}</b> doublons ignorés · <b>{importResult.total_errors}</b> erreurs</div>
          {importResult.errors?.length > 0 && (
            <div className="mt-1 text-rose-700">{importResult.errors.slice(0, 5).map((e, i)=>(<div key={`import-err-${i}`}>{e}</div>))}</div>
          )}
        </div>
      )}

      {/* Invite link */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="org-members-invite-block">
        <div className="flex items-center gap-2 mb-2"><LinkIcon size={16} className="text-emerald-600"/><h2 className="font-heading font-bold">Inviter mes membres</h2></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Lien d'inscription public</Label>
            <div className="flex gap-2 mt-1">
              <Input value={inviteLink} readOnly className="text-xs" data-testid="org-members-invite-link"/>
              <Button variant="outline" size="sm" onClick={copyLink} data-testid="org-members-copy-link"><Copy size={13}/></Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Partagez ce lien à vos membres pour qu'ils créent leur compte gratuitement.</p>
          </div>
          <form onSubmit={invite}>
            <Label className="text-xs">Ou envoyer une invitation par email</Label>
            <div className="flex gap-2 mt-1">
              <Input type="email" required value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} placeholder="membre@exemple.fr" data-testid="org-members-invite-email"/>
              <Button type="submit" size="sm" data-testid="org-members-invite-submit">Envoyer</Button>
            </div>
          </form>
        </div>
      </div>

      {/* Add form */}
      {addOpen && (
        <form onSubmit={addMember} className="mt-4 bg-white border border-emerald-300 rounded-2xl p-4 grid md:grid-cols-3 gap-3" data-testid="org-members-add-form">
          <div><Label>Prénom *</Label><Input required value={addForm.first_name} onChange={(e)=>setAddForm((s)=>({ ...s, first_name: e.target.value }))}/></div>
          <div><Label>Nom *</Label><Input required value={addForm.last_name} onChange={(e)=>setAddForm((s)=>({ ...s, last_name: e.target.value }))}/></div>
          <div><Label>Email *</Label><Input required type="email" value={addForm.email} onChange={(e)=>setAddForm((s)=>({ ...s, email: e.target.value }))}/></div>
          <div><Label>Téléphone</Label><Input value={addForm.phone} onChange={(e)=>setAddForm((s)=>({ ...s, phone: e.target.value }))}/></div>
          <div><Label>Réf. interne</Label><Input value={addForm.internal_ref} onChange={(e)=>setAddForm((s)=>({ ...s, internal_ref: e.target.value }))}/></div>
          <div className="flex items-end gap-2">
            <Button type="submit" disabled={busyId === "__new__"} className="btn-shine text-white flex-1" data-testid="org-members-add-submit">{busyId === "__new__" ? "..." : "Ajouter"}</Button>
            <Button type="button" variant="outline" onClick={()=>setAddOpen(false)}>Annuler</Button>
          </div>
        </form>
      )}

      {/* Tabs + search */}
      <div className="mt-5 flex flex-wrap gap-2 items-center">
        {[
          { k: "all", l: "Tout" },
          { k: "active", l: `Actifs${counts.active ? " · "+counts.active : ""}` },
          { k: "inactive", l: `Inactifs${counts.inactive ? " · "+counts.inactive : ""}` },
          { k: "suspended", l: `Suspendus${counts.suspended ? " · "+counts.suspended : ""}` },
        ].map((t)=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${tab === t.k ? "bg-emerald-600 text-white border-transparent" : "bg-white border-slate-200 text-slate-700"}`}
                  data-testid={`org-members-tab-${t.k}`}>{t.l}</button>
        ))}
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Rechercher..." className="pl-9" data-testid="org-members-search"/>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw size={13} className={loading ? "animate-spin" : ""}/></Button>
      </div>

      {/* List */}
      {loading ? <div className="mt-4 text-slate-500 text-sm">Chargement...</div> :
       filtered.length === 0 ? (
        <div className="mt-6 bg-white border border-dashed rounded-2xl p-10 text-center text-slate-500 text-sm">
          Aucun membre. Cliquez sur <b>Ajouter un membre</b> ou <b>Import CSV</b>.
        </div>
       ) : (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Nom</th>
                <th className="text-left px-3 py-2 font-bold">Email</th>
                <th className="text-left px-3 py-2 font-bold">Tél</th>
                <th className="text-left px-3 py-2 font-bold">Réf.</th>
                <th className="text-left px-3 py-2 font-bold">Statut</th>
                <th className="text-right px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m)=>(
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`org-member-row-${m.id}`}>
                  <td className="px-3 py-2"><b>{m.first_name} {m.last_name}</b></td>
                  <td className="px-3 py-2 text-slate-600">{m.email}</td>
                  <td className="px-3 py-2 text-slate-600">{m.phone || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{m.internal_ref || "—"}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${STATUS_STYLES[m.status] || ""}`}>{STATUS_LABELS[m.status] || m.status}</span></td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {m.status !== "active" && <button disabled={busyId === m.id} onClick={()=>changeStatus(m, "active")} title="Activer" className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700" data-testid={`org-member-activate-${m.id}`}><UserCheck size={14}/></button>}
                      {m.status !== "suspended" && <button disabled={busyId === m.id} onClick={()=>changeStatus(m, "suspended")} title="Suspendre" className="p-1.5 rounded hover:bg-rose-100 text-rose-700" data-testid={`org-member-suspend-${m.id}`}><PauseCircle size={14}/></button>}
                      {m.status !== "inactive" && <button disabled={busyId === m.id} onClick={()=>softDelete(m)} title="Désactiver" className="p-1.5 rounded hover:bg-slate-200 text-slate-700" data-testid={`org-member-deactivate-${m.id}`}><UserX size={14}/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      {/* CSV help */}
      <details className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs">
        <summary className="cursor-pointer font-semibold text-slate-700">Format du fichier CSV attendu</summary>
        <div className="mt-2 text-slate-600">
          <p>En-têtes acceptés (français ou anglais, séparateur <code>,</code> ou <code>;</code>) :</p>
          <ul className="list-disc list-inside mt-1">
            <li><b>prenom</b> / firstname · <b>nom</b> / lastname · <b>email</b> (obligatoire) · <b>telephone</b> · <b>reference</b> / matricule · <b>statut</b></li>
          </ul>
          <pre className="mt-2 bg-white border border-slate-200 rounded p-2 overflow-auto">prenom;nom;email;telephone;reference;statut
Marie;Dupont;marie@ex.fr;0237XXXXXX;M001;active
Jean;Martin;jean@ex.fr;;M002;active</pre>
        </div>
      </details>
    </div>
  );
}
